import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "./ConfirmDialog.jsx";
import { api } from "../api";

const FIELD_LABELS = {
  name: "ФИО", birthDate: "Дата рождения", phone: "Телефон", notes: "Заметки", crmLink: "Ссылка CRM",
  socialNetwork: "Соцсеть", internalNote: "Внутренняя заметка", location: "Точка",
  yclientsClientId: "Клиент YClients", yclientsCompanyId: "Филиал YClients",
  messenger: "Мессенджер", messengerLink: "Ссылка на мессенджер",
};
const BROKEN = "�";

function Value({ value }) {
  if (value === null || value === undefined || value === "") return <span className="text-slate-400">пусто</span>;
  const broken = String(value).includes(BROKEN);
  return <span className={broken ? "text-red-600 font-medium" : "text-slate-800"}>{String(value)}{broken && " — испорчено"}</span>;
}

function EditConflict({ item, confirm, onDone }) {
  const [choices, setChoices] = useState(() => Object.fromEntries(item.diffs.map((d) => [d.key, d.suggested])));
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!(await confirm(`Сохранить карточку «${item.name}» с выбранными значениями? Сканы и покупки не меняются.`, { confirmLabel: "Применить" }))) return;
    setBusy(true);
    try {
      const result = await api.resolveEditConflict(item.patientId, choices);
      onDone(result.sentToCentral ? "Конфликт разобран, карточка уйдёт на центр при синхронизации" : "Конфликт разобран");
    } catch (err) {
      onDone(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-sm font-medium text-slate-900 mb-0.5">{item.name}</div>
      <div className="text-[11px] text-slate-500 mb-2">Карточку изменили и на точке, и на центре. Выберите, какое значение оставить.</div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500"><th className="text-left font-medium py-1 pr-2">Поле</th><th className="text-left font-medium py-1 pr-2">На точке</th><th className="text-left font-medium py-1">На центре</th></tr>
        </thead>
        <tbody>
          {item.diffs.map((d) => (
            <tr key={d.key} className="border-t border-slate-100 align-top">
              <td className="py-1.5 pr-2 text-slate-600">{FIELD_LABELS[d.key] || d.key}</td>
              {["point", "central"].map((side) => (
                <td key={side} className="py-1.5 pr-2">
                  <label className="flex gap-1.5 items-start cursor-pointer">
                    <input type="radio" className="mt-0.5" name={`${item.patientId}-${d.key}`} checked={choices[d.key] === side}
                      onChange={() => setChoices((c) => ({ ...c, [d.key]: side }))} />
                    <span className="break-all"><Value value={d[side]} /></span>
                  </label>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <Button className="mt-2" size="sm" onClick={apply} disabled={busy}>{busy ? "Сохранение..." : "Применить"}</Button>
    </div>
  );
}

function DuplicateGroup({ group, confirm, onDone }) {
  const [survivorId, setSurvivorId] = useState(group.suggestedSurvivorId);
  const [busy, setBusy] = useState(false);
  const survivor = group.patients.find((p) => p.id === survivorId);

  const merge = async () => {
    const others = group.patients.length - 1;
    if (!(await confirm(
      `Объединить ${group.patients.length} карточки «${survivor?.fields.name}» в одну? Все обследования и покупки перейдут в выбранную карточку, заполненные данные из остальных сохранятся, а ${others === 1 ? "лишняя карточка будет удалена" : `лишние карточки (${others}) будут удалены`}.`,
      { confirmLabel: "Объединить" }
    ))) return;
    setBusy(true);
    try {
      const result = await api.mergeDuplicatePatients(group.serverId, survivorId);
      onDone(`Карточки объединены: перенесено обследований ${result.documents}, покупок ${result.purchases}`);
    } catch (err) {
      onDone(err.response?.data?.error || err.message, true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-sm font-medium text-slate-900 mb-0.5">{group.remote?.name || survivor?.fields.name}</div>
      <div className="text-[11px] text-slate-500 mb-2">На точке несколько карточек одного пациента. Выберите основную — остальные будут присоединены к ней.</div>
      <div className="flex flex-col gap-1.5">
        {group.patients.map((p) => (
          <label key={p.id} className={`flex gap-2 items-start rounded-md border px-2 py-1.5 cursor-pointer text-xs ${p.id === survivorId ? "border-blue-300 bg-blue-50" : "border-slate-200"}`}>
            <input type="radio" className="mt-0.5" name={`dup-${group.serverId}`} checked={p.id === survivorId} onChange={() => setSurvivorId(p.id)} />
            <span className="flex-1 min-w-0">
              <span className="block break-all"><Value value={p.fields.name} /></span>
              <span className="block text-slate-500">
                {p.fields.phone || "без телефона"} · обследований: {p.documents} · покупок: {p.purchases}
                {!p.synced && " · есть неотправленные изменения"}
              </span>
            </span>
          </label>
        ))}
      </div>
      <Button className="mt-2" size="sm" onClick={merge} disabled={busy || !survivorId}>{busy ? "Объединение..." : "Объединить"}</Button>
    </div>
  );
}

// Point admin only (backend routes/syncConflicts.js). Conflicts are flagged by importPatients()
// during sync; without this panel they block a card forever with «требуется проверка».
export default function SyncConflictsPanel({ notify, onResolved }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const { confirm, ConfirmDialogElement } = useConfirm();

  // No props in deps: the parent's toast callback is recreated on every render.
  const load = useCallback(() => api.listSyncConflicts()
    .then((result) => { setData(result); setLoadError(""); })
    .catch((err) => setLoadError(err.response?.data?.error || err.message)), []);

  useEffect(() => { load(); }, [load]);

  const handleDone = async (message, failed) => {
    notify?.(message, { type: failed ? "error" : "success" });
    await load();
    if (!failed) onResolved?.();
  };

  if (loadError) return <p className="mt-3 text-[11px] text-red-600">Не удалось загрузить конфликты пациентов: {loadError}</p>;
  if (!data) return null;
  const count = data.edits.length + data.duplicates.length;
  if (!count && !data.other.length) return null;

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      <ConfirmDialogElement />
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Конфликты пациентов: {count}</h4>
      <p className="text-[11px] text-slate-500 mb-3">Перед изменениями автоматически сохраняется копия базы точки. Обследования и покупки не удаляются.</p>
      <div className="flex flex-col gap-3">
        {data.edits.map((item) => <EditConflict key={item.patientId} item={item} confirm={confirm} onDone={handleDone} />)}
        {data.duplicates.map((group) => <DuplicateGroup key={group.serverId} group={group} confirm={confirm} onDone={handleDone} />)}
        {data.other.map((item) => (
          <div key={item.patientId} className="text-xs text-slate-500">{item.name}: конфликт типа «{item.type}» — сообщите разработчику.</div>
        ))}
      </div>
    </div>
  );
}
